'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Accordion as AccordionPrimitive } from '@base-ui-components/react/accordion';
import { type ClassValue, clsxm } from '@vs-shop/utils';
import { ChevronDownIcon } from 'lucide-react';

export const Accordion = (props: ComponentProps<typeof AccordionPrimitive.Root>) => (
  <AccordionPrimitive.Root data-slot="accordion" className="w-full" {...props} />
);

/**
 * Which ground the accordion stands on, which is the one thing about it that
 * cannot be inherited: the rule between rows and the ink on the trigger both
 * name a colour, so a dark band needs its own arm rather than a class per row.
 */
export const accordionItemVariants = cva('border-foreground/10 border-b last:border-b-0', {
  variants: {
    variant: {
      /** On the page's own ground. */
      plain: '',
      /** On a dark band, where the rule has to be drawn in the inverted ink. */
      inverted: 'border-foreground-inverted/10',
      /** Adds nothing to the base rule; a tenant restyles it through `className`. */
      unstyled: '',
    },
  },
});

type AccordionItemVariantName = NonNullable<VariantProps<typeof accordionItemVariants>['variant']>;

export type AccordionItemVariant = AccordionItemVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AccordionItemVariant = {
  /** On the page's own ground. */
  PLAIN: 'plain',
  /** On a dark band. */
  INVERTED: 'inverted',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, AccordionItemVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AccordionItemVariantProps = {
  readonly variant: AccordionItemVariant;
};

export type AccordionItemProps = ComponentProps<typeof AccordionPrimitive.Item> &
  AccordionItemVariantProps;

export const AccordionItem = ({ className, variant, ...props }: AccordionItemProps) => (
  <AccordionPrimitive.Item
    data-slot="accordion-item"
    className={clsxm(accordionItemVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The trigger's ink, on the same axis as the item's rule: the chevron follows
 * the label, because the two are one control and a caller colouring only half
 * of it is the bug this axis exists to prevent.
 */
export const accordionTriggerVariants = cva('', {
  variants: {
    variant: {
      /** On the page's own ground. */
      plain: '',
      /** On a dark band. */
      inverted: 'text-foreground-inverted',
      /** The opt-out: a tenant inking the trigger itself. */
      unstyled: '',
    },
  },
});

/** The chevron, on that same axis. */
const accordionTriggerIconVariants = cva('', {
  variants: {
    variant: {
      plain: 'text-foreground-muted',
      inverted: 'text-foreground-inverted',
      unstyled: '',
    },
  },
});

export type AccordionTriggerProps = ComponentProps<typeof AccordionPrimitive.Trigger> &
  AccordionItemVariantProps & {
    iconClassName?: ClassValue;
  };

export const AccordionTrigger = ({
  className,
  variant,
  iconClassName,
  children,
  ...props
}: AccordionTriggerProps) => (
  <AccordionPrimitive.Header className="lap:hover:underline flex w-full">
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      className={clsxm(
        'flex w-full items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none',
        'focus-visible:border-foreground focus-visible:ring-foreground/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50 [&[data-panel-open]>svg]:rotate-180',
        accordionTriggerVariants({ variant }),
        className
      )}
      {...props}
    >
      <span className="flex flex-1">{children}</span>
      <ChevronDownIcon
        className={clsxm(
          'size-4 shrink-0 translate-y-0.5 transition-transform duration-200',
          accordionTriggerIconVariants({ variant }),
          iconClassName
        )}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

// Base UI calls it Panel (Radix's Content). Kept under the shadcn-familiar
// `AccordionContent` name for the public API. The open state is exposed on the
// trigger as `data-panel-open` (Radix's `data-state=open`) for chevron rotation;
// the panel animates its height via Base UI's `--accordion-panel-height` var and
// the `data-starting-style` / `data-ending-style` transition hooks.
export const AccordionContent = ({
  className,
  children,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Panel>) => (
  <AccordionPrimitive.Panel
    data-slot="accordion-content"
    className={clsxm(
      'h-(--accordion-panel-height) overflow-hidden text-sm transition-[height] duration-200 ease-out',
      'data-ending-style:h-0 data-starting-style:h-0'
    )}
    {...props}
  >
    <div className={clsxm('mb-4', className)}>{children}</div>
  </AccordionPrimitive.Panel>
);
